import React from "react";
import {
  CheckCircle,
  Leaf,
  HeartHandshake,
  PackageSearch,
  Globe,
  FlaskConical,
  ShoppingBag,
  ShieldCheck,
  BadgePercent,
  HelpingHand,
  UsersRound,
  Sprout,
  Factory,
  Package,
  Star,
} from "lucide-react";
import { Button } from "../components/ui/button";
import FactoryImg from "../assets/img/factory.png";
import { motion } from "framer-motion";

const handleClick = () => {
  const message = `Hello, I'm interested in discussing B2B opportunities and potential collaboration. Please let me know how we can proceed.`;

  const whatsappURL = `https://wa.me/919151816021?text=${encodeURIComponent(
    message
  )}`;

  window.open(whatsappURL, "_blank");
};
const handleClickQuote = () => {
  const message = `Hello! I’m excited to learn more about your product range. Could you please provide details on bulk ordering options and any special offers available? Looking forward to discussing how we can collaborate and grow together!`;

  const whatsappURL = `https://wa.me/919151816021?text=${encodeURIComponent(
    message
  )}`;

  window.open(whatsappURL, "_blank");
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, when: "beforeChildren" },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Services() {
  return (
    <div className="flex flex-col">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <motion.section
        className="relative w-full overflow-hidden bg-gradient-to-br from-[#fdf8f4] to-white py-20"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="container mx-auto px-6 flex flex-col lg:flex-row items-center gap-12">
          <motion.div
            className="max-w-xl"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight text-gray-900 mb-6">
              Nature‑Powered{" "}
              <span className="text-[#f98715]">Pet Wellness</span> & B2B
              Solutions
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Herbal, vegan, cruelty‑free products crafted with Ayurvedic wisdom
              & modern science. Scale your brand globally with our OEM,
              private‑label and bulk supply services.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ display: "inline-block" }}
            >
              <Button
                size="lg"
                className="rounded-2xl px-8 py-5 text-lg bg-[#f98715] hover:bg-[#f98715] transition"
                onClick={handleClickQuote}
              >
                Get a Quote
              </Button>
            </motion.div>
          </motion.div>
          <motion.div
            className="relative flex-1 flex justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.img
              src="https://adinsinternational.com/websitebackend/upload/1747680217_2485_Multivitamins_Chews.png"
              alt="Adins products"
              className="w-full max-w-md lg:max-w-lg rounded-2xl shadow-2xl object-contain"
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </motion.div>
        </div>
      </motion.section>

      {/* ── FEATURES GRID ─────────────────────────────────────── */}
      <section className="container mx-auto px-6 py-20">
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Why Partner with{" "}
          <span className="text-[#f98715]">Adins International</span>
        </motion.h2>
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {[
            {
              icon: Leaf,
              title: "Herbal, Plant‑Based",
              desc: "100% vegan, cruelty‑free and safe.",
            },
            {
              icon: CheckCircle,
              title: "Vet‑Approved",
              desc: "Research‑backed formulas for proven efficacy.",
            },
            {
              icon: ShieldCheck,
              title: "Certified Manufacturing",
              desc: "ISO & GMP compliant facilities.",
            },
            {
              icon: PackageSearch,
              title: "OEM / Private Label",
              desc: "Custom formulations & branding support.",
            },
            {
              icon: Globe,
              title: "Global Export Expertise",
              desc: "Multilingual labels & regulatory compliance.",
            },
            {
              icon: Sprout,
              title: "Sustainable Sourcing",
              desc: "Eco‑friendly ingredients & packaging.",
            },
            {
              icon: HelpingHand,
              title: "Dedicated B2B Support",
              desc: "Responsive communication & R&D assistance.",
            },
            {
              icon: HeartHandshake,
              title: "Ethical Commitment",
              desc: "No animal testing, fair supply chains.",
            },
          ].map(({ icon: Icon, title, desc }) => (
            <motion.div
              key={title}
              className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition"
              variants={itemVariants}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 4px rgba(249, 135, 21, 0.4)",
              }}
            >
              <Icon size={40} className="text-[#f98715] mb-4" />
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                {title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── PRODUCT RANGE SECTION ─────────────────────────────── */}
      <section className="bg-[#fdf5ec] py-20">
        <div className="container mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            Our Product <span className="text-[#f98715]">Portfolio</span>
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              {
                icon: FlaskConical,
                title: "Herbal Supplements",
                desc: "Immunity, digestion, joints, skin & calm.",
              },
              {
                icon: ShoppingBag,
                title: "Vegan Treats",
                desc: "Dental chews & protein‑rich snacks.",
              },
              {
                icon: Package,
                title: "Complete Vegan Food",
                desc: "Balanced, 100% vegetarian meals.",
              },
              {
                icon: Star,
                title: "Yak Milk Chews",
                desc: "Long‑lasting, preservative‑free chews.",
              },
              {
                icon: BadgePercent,
                title: "Catnip Toys",
                desc: "Engaging, natural catnip range.",
              },
              {
                icon: UsersRound,
                title: "Eco Cat Litter",
                desc: "Herbal & clay‑based options.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <motion.div
                key={title}
                className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition"
                variants={itemVariants}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 10px 4px rgba(249, 135, 21, 0.4)",
                }}
              >
                <Icon size={36} className="text-[#f98715] mb-3" />
                <h3 className="text-xl font-semibold mb-2 text-gray-800">
                  {title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PACKAGING & PRINTING ─────────────────────────────── */}
      <section className="container mx-auto px-6 py-20">
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          End‑to‑End <span className="text-[#f98715]">Packaging Solutions</span>
        </motion.h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            className="space-y-6 mt-4"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-xl font-semibold flex items-center gap-3">
              <Factory className="text-[#f98715]" /> Packaging Essentials
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              Stand‑up pouches, jars, bottles, sachets & bulk bags. BPA‑free,
              eco‑friendly layers with resealable closures.
            </p>
            <h3 className="text-xl font-semibold flex items-center gap-3">
              <Package className="text-[#f98715]" /> Customized Printing
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              Digital, flexographic, gravure & UV printing. Waterproof,
              scratch‑resistant finishes, QR codes & multilingual labels.
            </p>
          </motion.div>
          <motion.img
            src={FactoryImg}
            alt="Packaging"
            className="rounded-2xl shadow-xl object-cover h-72 w-full"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          />
        </div>
      </section>

      {/* ── SUSTAINABILITY ───────────────────────────────────── */}
      <section className="bg-[#fdf5ec] py-20">
        <div className="container mx-auto px-6 flex flex-col lg:flex-row items-center gap-14">
          <motion.img
            src="https://images.pexels.com/photos/955395/pexels-photo-955395.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
            alt="Sustainability"
            className="rounded-2xl shadow-2xl object-cover w-full max-w-lg"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          />
          <motion.div
            className="flex-1 space-y-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              B2B Partnership Opportunities
            </h2>
            <p className="text-gray-700 leading-relaxed text-sm">
              Adins International proudly offers OEM, private label, and bulk
              supply services for businesses across the pet care industry.
              Whether you're a brand, distributor, or retailer, we provide:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm text-gray-600">
              <li>Custom Herbal Formulations based on your market needs</li>
              <li>White Label Solutions with full branding support</li>
              <li>Flexible MOQ and global shipping options</li>
              <li>
                Regulatory Compliance & Certifications for international markets
              </li>
              <li>Veterinary-backed product development and R&D support</li>
            </ul>
            <p className="text-gray-800 text-lg leading-relaxed mt-6 text-center max-w-3xl mx-auto">
              Join hands with a trusted Indian manufacturer known for
              innovation, integrity, and sustainable pet care solutions. Partner
              with Adins International Pvt. Ltd. to bring safe, plant-powered
              wellness products to pets worldwide.
            </p>
            <p className="text-2xl font-semibold  text-[#f98715] mt-8 text-left">
              📦 Let’s build a healthier world for pets—together
            </p>
          </motion.div>
        </div>
      </section>
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{ display: "inline-block" }}
      >
        <section className="container mx-auto px-6 py-20">
          <div
            className="bg-[#f98715] rounded-3xl px-10 py-14 text-center text-white shadow-2xl"
            data-aos="zoom-in"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
              Ready to Elevate Your Brand?
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Partner with Adins International for custom formulations, private
              label solutions and global export support.
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="bg-white text-[#f98715] hover:bg-gray-100 font-semibold rounded-xl px-8 py-5"
              onClick={handleClick}
            >
              Contact Our B2B Team
            </Button>
          </div>
        </section>
      </motion.div>
    </div>
  );
}
